import type { GrammarExercise } from "./grammar.js";

export const VERB_EXERCISES: Record<string, GrammarExercise[]> = {
  "modal-verbs": [
    {
      question: "You look really tired. You ___ go to bed early tonight.",
      options: ["must", "might", "can't", "should"],
      correctIndex: 3,
      explanationVi:
        "Đây là lời khuyên nhẹ nhàng nên dùng should; must mang tính bắt buộc mạnh, không hợp với ngữ cảnh khuyên bạn bè.",
    },
    {
      question: "Employees ___ wear a uniform at this restaurant — it's a company rule.",
      options: ["should", "might", "have to", "could"],
      correctIndex: 2,
      explanationVi:
        "Nghĩa vụ đến từ bên ngoài (it's a company rule) dùng have to; should chỉ là lời khuyên, không diễn tả quy định bắt buộc.",
    },
    {
      question: "The meeting is optional, so you ___ attend if you're busy.",
      options: ["mustn't", "can't", "shouldn't", "don't have to"],
      correctIndex: 3,
      explanationVi:
        "'Optional' cho biết việc tham dự không bắt buộc, dùng don't have to (không cần); mustn't lại có nghĩa là bị cấm.",
    },
    {
      question: "She ___ speak any English when she moved here, but now she's fluent.",
      options: ["couldn't", "mustn't", "shouldn't", "may not"],
      correctIndex: 0,
      explanationVi:
        "Nói về khả năng trong quá khứ (when she moved here) dùng could/couldn't — dạng quá khứ của can.",
    },
    {
      question: "The lights are off and the car is gone. They ___ be at home.",
      options: ["must", "can't", "have to", "should"],
      correctIndex: 1,
      explanationVi:
        "Suy luận phủ định gần như chắc chắn dựa trên dấu hiệu (đèn tắt, xe không có) dùng can't; must dùng cho suy luận khẳng định.",
    },
  ],
  "passive-voice": [
    {
      question: "This bridge ___ over fifty years ago.",
      options: ["built", "has been built", "is built", "was built"],
      correctIndex: 3,
      explanationVi:
        "Từ nhận biết 'ago' chỉ quá khứ xác định nên dùng bị động thì quá khứ đơn: was/were + V3.",
    },
    {
      question: "The new office ___ right now, so we are working from home.",
      options: ["is built", "is being built", "has been built", "was built"],
      correctIndex: 1,
      explanationVi:
        "'Right now' cho biết hành động đang diễn ra, dùng bị động hiện tại tiếp diễn: is/are + being + V3.",
    },
    {
      question: "Millions of emails ___ every day around the world.",
      options: ["are sent", "send", "are being sent", "were sent"],
      correctIndex: 0,
      explanationVi:
        "'Every day' là dấu hiệu của hiện tại đơn; emails là đối tượng chịu tác động nên dùng bị động: are + V3.",
    },
    {
      question: "The report ___ yet, so please wait a little longer.",
      options: ["wasn't finished", "isn't finished", "hasn't been finished", "hadn't been finished"],
      correctIndex: 2,
      explanationVi:
        "Từ nhận biết 'yet' đi với hiện tại hoàn thành, dạng bị động là has/have + been + V3.",
    },
    {
      question: "The winners of the competition ___ next Friday.",
      options: ["announce", "are announced", "will be announced", "have been announced"],
      correctIndex: 2,
      explanationVi:
        "'Next Friday' chỉ tương lai, và người thắng cuộc được công bố (bị động), nên dùng will + be + V3.",
    },
  ],
  conditionals: [
    {
      question: "If I ___ you, I would ask the boss for a pay rise.",
      options: ["am", "was", "were", "had been"],
      correctIndex: 2,
      explanationVi:
        "Câu điều kiện loại 2 (giả định trái hiện tại, có would ở mệnh đề chính) dùng 'were' cho mọi chủ ngữ với động từ to be.",
    },
    {
      question: "If it rains this weekend, we ___ the picnic.",
      options: ["would cancel", "cancel", "would have cancelled", "will cancel"],
      correctIndex: 3,
      explanationVi:
        "Điều kiện có thể xảy ra ở tương lai (this weekend) là loại 1: If + hiện tại đơn, S + will + V.",
    },
    {
      question: "If she had left the house earlier, she ___ the train.",
      options: ["would catch", "will catch", "would have caught", "had caught"],
      correctIndex: 2,
      explanationVi:
        "Mệnh đề if dùng had + V3 cho thấy đây là loại 3 (trái với quá khứ), mệnh đề chính phải là would have + V3.",
    },
    {
      question: "If you ___ water to 100 degrees, it boils.",
      options: ["heat", "will heat", "heated", "would heat"],
      correctIndex: 0,
      explanationVi:
        "Sự thật khoa học hiển nhiên dùng câu điều kiện loại 0: cả hai mệnh đề đều ở hiện tại đơn.",
    },
    {
      question: "If I hadn't skipped breakfast this morning, I ___ so hungry now.",
      options: ["wouldn't have been", "wouldn't be", "won't be", "am not"],
      correctIndex: 1,
      explanationVi:
        "Điều kiện thuộc quá khứ (hadn't skipped) nhưng kết quả ở hiện tại (now) là câu điều kiện hỗn hợp: would + V nguyên thể.",
    },
  ],
  "reported-speech": [
    {
      question: "\"I am busy today,\" he said. → He said that he ___ busy that day.",
      options: ["is", "has been", "were", "was"],
      correctIndex: 3,
      explanationVi:
        "Trong câu tường thuật, thì lùi một bậc: hiện tại đơn (am) lùi thành quá khứ đơn (was); 'today' đổi thành 'that day'.",
    },
    {
      question: "\"Where do you work?\" she asked me. → She asked me where ___.",
      options: ["do I work", "did I work", "I worked", "I work"],
      correctIndex: 2,
      explanationVi:
        "Câu hỏi Wh- khi tường thuật phải bỏ đảo ngữ (không dùng do/did trước chủ ngữ) và lùi thì: work → worked.",
    },
    {
      question: "\"Don't touch the machine,\" the manager said. → The manager told us ___ the machine.",
      options: ["don't touch", "not to touch", "to not touching", "didn't touch"],
      correctIndex: 1,
      explanationVi:
        "Câu mệnh lệnh phủ định khi tường thuật dùng cấu trúc told + O + not + to V.",
    },
    {
      question: "\"I will call you tomorrow,\" she said. → She said she ___ me the next day.",
      options: ["will call", "called", "would have called", "would call"],
      correctIndex: 3,
      explanationVi:
        "Khi lùi thì trong câu tường thuật, will đổi thành would; 'tomorrow' đổi thành 'the next day'.",
    },
    {
      question: "\"Have you finished the report?\" he asked. → He asked me ___ the report.",
      options: ["if I had finished", "if I have finished", "that I finished", "did I finish"],
      correctIndex: 0,
      explanationVi:
        "Câu hỏi Yes/No tường thuật dùng if/whether, và hiện tại hoàn thành (have finished) lùi thành quá khứ hoàn thành (had finished).",
    },
  ],
  "gerunds-infinitives": [
    {
      question: "I really enjoy ___ with my colleagues after work.",
      options: ["to chat", "chat", "to chatting", "chatting"],
      correctIndex: 3,
      explanationVi:
        "Động từ enjoy luôn theo sau bởi V-ing, không dùng to V.",
    },
    {
      question: "She decided ___ a new apartment closer to her office.",
      options: ["renting", "rent", "to rent", "to renting"],
      correctIndex: 2,
      explanationVi:
        "Động từ decide thuộc nhóm theo sau bởi to V (decide, want, plan, hope...).",
    },
    {
      question: "Don't forget ___ the report before you leave — it's due today.",
      options: ["sending", "to send", "send", "to sending"],
      correctIndex: 1,
      explanationVi:
        "Forget + to V nghĩa là quên làm việc chưa xảy ra (việc cần làm); forget + V-ing là quên việc đã làm rồi. Ở đây việc gửi báo cáo chưa xảy ra.",
    },
    {
      question: "He stopped ___ coffee because it kept him awake at night.",
      options: ["drinking", "to drink", "drink", "to drinking"],
      correctIndex: 0,
      explanationVi:
        "Stop + V-ing nghĩa là dừng hẳn việc đang làm (bỏ uống cà phê); stop + to V là dừng lại để làm việc khác.",
    },
    {
      question: "She's interested in ___ for a foreign company someday.",
      options: ["work", "to work", "working", "worked"],
      correctIndex: 2,
      explanationVi:
        "Sau giới từ (in, at, of, before, after...) luôn dùng V-ing: interested in working.",
    },
  ],
  "causative-form": [
    {
      question: "We had our house ___ last month, and it looks much brighter now.",
      options: ["paint", "to paint", "painting", "painted"],
      correctIndex: 3,
      explanationVi:
        "Cấu trúc nhờ người khác làm gì cho vật: have + O (vật) + V3 — ngôi nhà được sơn nên dùng painted.",
    },
    {
      question: "I'll get the technician ___ the printer this afternoon.",
      options: ["fix", "to fix", "fixed", "fixing"],
      correctIndex: 1,
      explanationVi:
        "Với tân ngữ chỉ người, cấu trúc là get + someone + to V; còn have + someone + V nguyên thể (không to).",
    },
    {
      question: "She had her assistant ___ the meeting room for tomorrow.",
      options: ["to book", "booked", "book", "booking"],
      correctIndex: 2,
      explanationVi:
        "Cấu trúc have + someone (người) + V nguyên thể không to: nhờ một người cụ thể làm việc gì.",
    },
    {
      question: "You should get your eyes ___ at least once a year.",
      options: ["test", "to test", "testing", "tested"],
      correctIndex: 3,
      explanationVi:
        "Get + O (vật) + V3 diễn tả việc nhờ/thuê người khác làm: mắt được kiểm tra nên dùng tested.",
    },
    {
      question: "He ___ his phone screen replaced at the shop yesterday.",
      options: ["got", "made", "let", "took"],
      correctIndex: 0,
      explanationVi:
        "Dạng 'O + replaced' (V3) cho biết đây là câu nhờ làm hộ, chỉ have/get đi được với cấu trúc này: got something done.",
    },
  ],
  "question-tags": [
    {
      question: "You've met our new manager before, ___?",
      options: ["didn't you", "haven't you", "don't you", "hadn't you"],
      correctIndex: 1,
      explanationVi:
        "Mệnh đề chính dùng hiện tại hoàn thành khẳng định (you've met) nên câu hỏi đuôi dùng trợ động từ have ở dạng phủ định: haven't you.",
    },
    {
      question: "She doesn't eat meat, ___?",
      options: ["doesn't she", "isn't she", "does she", "is she"],
      correctIndex: 2,
      explanationVi:
        "Câu phủ định (doesn't) đi với đuôi khẳng định, và trợ động từ phải khớp: does she.",
    },
    {
      question: "Let's take a short break, ___?",
      options: ["will you", "shall we", "don't we", "won't we"],
      correctIndex: 1,
      explanationVi:
        "Câu bắt đầu bằng Let's luôn dùng câu hỏi đuôi 'shall we?'; 'will you?' dành cho câu mệnh lệnh.",
    },
    {
      question: "I'm late for the meeting again, ___?",
      options: ["amn't I", "don't I", "am I", "aren't I"],
      correctIndex: 3,
      explanationVi:
        "'I am' là trường hợp đặc biệt: câu hỏi đuôi là 'aren't I?' vì tiếng Anh không có dạng 'amn't I'.",
    },
    {
      question: "They went home early yesterday, ___?",
      options: ["didn't they", "don't they", "hadn't they", "weren't they"],
      correctIndex: 0,
      explanationVi:
        "Động từ thường ở quá khứ đơn (went) mượn trợ động từ did, câu khẳng định đi với đuôi phủ định: didn't they.",
    },
  ],
  articles: [
    {
      question: "She works as ___ engineer in Ho Chi Minh City.",
      options: ["a", "an", "the", "(no article)"],
      correctIndex: 1,
      explanationVi:
        "'Engineer' bắt đầu bằng nguyên âm khi phát âm nên dùng 'an'; nghề nghiệp nhắc lần đầu dùng mạo từ không xác định.",
    },
    {
      question: "I bought a shirt and a hat yesterday. ___ shirt was quite cheap.",
      options: ["A", "An", "The", "(no article)"],
      correctIndex: 2,
      explanationVi:
        "Danh từ đã được nhắc đến trước đó (a shirt) thì lần sau dùng 'the' vì cả hai bên đều biết đang nói về cái nào.",
    },
    {
      question: "I usually have ___ breakfast at 7 a.m. before going to work.",
      options: ["a", "an", "the", "(no article)"],
      correctIndex: 3,
      explanationVi:
        "Tên bữa ăn (breakfast, lunch, dinner) nói chung không dùng mạo từ (zero article).",
    },
    {
      question: "Look at ___ moon tonight — it's so bright!",
      options: ["a", "an", "the", "(no article)"],
      correctIndex: 2,
      explanationVi:
        "Những vật duy nhất (the moon, the sun, the earth) luôn dùng 'the' vì ai cũng biết rõ đối tượng.",
    },
    {
      question: "We had ___ really good lunch at that new restaurant.",
      options: ["a", "an", "the", "(no article)"],
      correctIndex: 0,
      explanationVi:
        "Bữa ăn bình thường không có mạo từ, nhưng khi có tính từ mô tả (really good) thì dùng a/an: a really good lunch.",
    },
  ],
  "countable-uncountable": [
    {
      question: "There isn't ___ milk left in the fridge.",
      options: ["many", "a few", "a", "much"],
      correctIndex: 3,
      explanationVi:
        "'Milk' là danh từ không đếm được nên đi với much trong câu phủ định; many và a few chỉ dùng cho danh từ đếm được.",
    },
    {
      question: "Can you give me ___ advice about my CV?",
      options: ["an", "a few", "some", "many"],
      correctIndex: 2,
      explanationVi:
        "'Advice' là danh từ không đếm được, không dùng a/an; 'some' dùng được cho cả danh từ không đếm được.",
    },
    {
      question: "How ___ chairs do we need for the meeting tomorrow?",
      options: ["much", "many", "little", "a lot"],
      correctIndex: 1,
      explanationVi:
        "'Chairs' là danh từ đếm được số nhiều nên hỏi số lượng bằng how many; how much dành cho danh từ không đếm được.",
    },
    {
      question: "She gave me two useful ___ of information.",
      options: ["piece", "cups", "slices", "pieces"],
      correctIndex: 3,
      explanationVi:
        "Để đếm danh từ không đếm được như information, dùng đơn vị 'piece of'; có 'two' nên phải ở số nhiều: pieces.",
    },
    {
      question: "We only have ___ time before the train leaves, so hurry up.",
      options: ["a few", "few", "a little", "many"],
      correctIndex: 2,
      explanationVi:
        "'Time' là danh từ không đếm được nên dùng a little (một chút); a few chỉ đi với danh từ đếm được số nhiều.",
    },
  ],
  "plural-nouns": [
    {
      question: "There are two ___ waiting outside the office.",
      options: ["womans", "women", "womens", "woman"],
      correctIndex: 1,
      explanationVi:
        "'Woman' là danh từ bất quy tắc: số nhiều đổi thành women, không thêm -s.",
    },
    {
      question: "All the ___ in this box are broken.",
      options: ["watchs", "watches", "watchies", "watch"],
      correctIndex: 1,
      explanationVi:
        "Danh từ tận cùng bằng -ch thêm -es để tạo số nhiều: watch → watches.",
    },
    {
      question: "Three ___ are playing football in the park.",
      options: ["childs", "childrens", "children", "child"],
      correctIndex: 2,
      explanationVi:
        "'Child' là danh từ bất quy tắc, số nhiều là children; 'childrens' sai vì children đã là dạng số nhiều rồi.",
    },
    {
      question: "We visited several ___ during our trip to Europe.",
      options: ["cities", "citys", "cityes", "city"],
      correctIndex: 0,
      explanationVi:
        "Danh từ tận cùng bằng phụ âm + y đổi y thành i rồi thêm -es: city → cities.",
    },
    {
      question: "My ___ hurt after walking around the city all day.",
      options: ["foots", "feets", "foot", "feet"],
      correctIndex: 3,
      explanationVi:
        "'Foot' là danh từ bất quy tắc: số nhiều là feet (foot → feet, giống tooth → teeth).",
    },
  ],
  pronouns: [
    {
      question: "This umbrella isn't ___ — mine is blue, not black.",
      options: ["your", "yours", "you", "yourself"],
      correctIndex: 1,
      explanationVi:
        "Đại từ sở hữu 'yours' đứng độc lập không kèm danh từ; 'your' là tính từ sở hữu nên phải có danh từ theo sau.",
    },
    {
      question: "My grandmother lives by ___ in a small town near the sea.",
      options: ["her", "hers", "herself", "she"],
      correctIndex: 2,
      explanationVi:
        "Cụm 'by + đại từ phản thân' (by herself) nghĩa là sống một mình; herself là đại từ phản thân của she.",
    },
    {
      question: "Anna and I are old friends. ___ went to the same school together.",
      options: ["We", "Us", "Our", "Them"],
      correctIndex: 0,
      explanationVi:
        "'Anna and I' làm chủ ngữ nên thay bằng đại từ chủ ngữ We; Us là đại từ tân ngữ, không đứng làm chủ ngữ.",
    },
    {
      question: "The manager wants to see you and ___ after lunch.",
      options: ["I", "my", "myself", "me"],
      correctIndex: 3,
      explanationVi:
        "Sau động từ 'see', 'you and ___' là tân ngữ nên dùng đại từ tân ngữ me, không dùng đại từ chủ ngữ I.",
    },
    {
      question: "___ left a message for you, but they didn't give a name.",
      options: ["Anyone", "Someone", "No one", "Everything"],
      correctIndex: 1,
      explanationVi:
        "Câu khẳng định nói về một người không xác định dùng someone; anyone thường dùng trong câu hỏi và câu phủ định.",
    },
  ],
  "relative-clauses": [
    {
      question: "The woman ___ sits next to me at work is from Da Nang.",
      options: ["which", "who", "whose", "where"],
      correctIndex: 1,
      explanationVi:
        "Danh từ đứng trước chỉ người (the woman) và đóng vai trò chủ ngữ của mệnh đề quan hệ nên dùng who; which chỉ dùng cho vật.",
    },
    {
      question: "My laptop, ___ I bought two years ago, still works very well.",
      options: ["that", "who", "which", "whose"],
      correctIndex: 2,
      explanationVi:
        "Mệnh đề quan hệ không xác định (có dấu phẩy) không được dùng that; laptop là vật nên dùng which.",
    },
    {
      question: "That's the café ___ we first met five years ago.",
      options: ["which", "that", "when", "where"],
      correctIndex: 3,
      explanationVi:
        "'The café' chỉ nơi chốn và mệnh đề sau đã đủ chủ ngữ - tân ngữ (we met), nên dùng trạng từ quan hệ where.",
    },
    {
      question: "I have a friend ___ brother is a famous chef in Hanoi.",
      options: ["whose", "who", "whom", "which"],
      correctIndex: 0,
      explanationVi:
        "Quan hệ sở hữu (người anh CỦA người bạn) dùng whose + danh từ: whose brother.",
    },
    {
      question: "The email ___ you sent me yesterday was very clear.",
      options: ["who", "where", "that", "whose"],
      correctIndex: 2,
      explanationVi:
        "'The email' là vật trong mệnh đề quan hệ xác định (không dấu phẩy) nên dùng that (hoặc which); who chỉ dùng cho người.",
    },
  ],
};
